import '@xyflow/react/dist/style.css';

import { Box, Button, IconButton, SvgIcon, Toolbar, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import ChatFlowEdge from 'components/@home/@settings/Tabs/ChatFlowEdge';
import ChatFlowNode from 'components/@home/@settings/Tabs/ChatFlowNode';
import connector from 'components/@home/@settings/Tabs/connector';
import getLayoutedElements from 'components/@home/@settings/Tabs/getLayoutedElements';
import AlertCircleCheckOutlineIcon from 'mdi-react/AlertCircleCheckOutlineIcon';
import BrainFreezeOutlineIcon from 'mdi-react/BrainFreezeOutlineIcon';
import CommentTextMultipleIcon from 'mdi-react/CommentTextMultipleIcon';
import FitToPageOutlineIcon from 'mdi-react/FitToPageOutlineIcon';
import GraphOutlineIcon from 'mdi-react/GraphOutlineIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import ZoomInOutlineIcon from 'mdi-react/ZoomInOutlineIcon';
import ZoomOutOutlineIcon from 'mdi-react/ZoomOutOutlineIcon';
import { object } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const nodeTypes = { page: ChatFlowNode };
const edgeTypes = { route: ChatFlowEdge };

const getStyles = theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  toolbar: {
    minHeight: 49,
    overflow: 'hidden',
  },
  flow: {
    flex: 1,
    boxShadow: `0 0 0 0.5px ${theme.palette.secondary.light}`,
    '& .react-flow__node-page': {
      padding: 0,
      border: 0,
      minWidth: 150,
    },
  },
  background: { backgroundColor: theme.palette.secondary.ultraUltraLight },
  toolbarCenter: {
    flexGrow: 1,
  },
  trainingIcon: {
    fontSize: 18,
    marginRight: '5px',
    color: theme.palette.primary.main,
    '@keyframes chatFlowTrainingSpin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '50%': {
        transform: 'rotate(360deg)',
      },
      '100%': {
        transform: 'rotate(720deg)',
      },
    },
    animationName: 'chatFlowTrainingSpin',
    animationDuration: '2s',
    animationTimingFunction: theme.transitions.easing.easeIn,
    animationIterationCount: 'infinite',
  },
  icon: {
    fontSize: 18,
    marginRight: '5px',
    color: theme.palette.primary.main,
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
  },
  warningIcon: {
    fontSize: 15,
    color: theme.palette.danger,
    marginRight: '5px',
  },
});

const position = { x: 0, y: 0 };

const buildFlowGraph = flow => {
  const nodes = [];
  const edges = [];
  if (!flow?.pages) {
    return { nodes, edges };
  }
  flow.pages.forEach(page => {
    nodes.push({
      id: page._id,
      type: 'page',
      data: {
        page,
        startPage: flow.startPage,
      },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      position,
    });
    page.routes.forEach(r => {
      if (r.transition && r.transition !== flow.startPage) {
        edges.push({
          id: r._id,
          type: 'route',
          source: page._id,
          target: r.transition,
          markerEnd: { type: MarkerType.Arrow },
          sourceHandle: r._id,
        });
      }
    });
  });
  return { nodes, edges };
};

const ChatFlowEdit = ({ actions, flow }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { zoomIn, zoomOut, fitView, setCenter, getNode } = useReactFlow();
  const { context } = useContext(ChatFlowContext);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [layoutState, setLayoutState] = useState('not done');
  const [flowReady, setFlowReady] = useState(false);

  const onNodesChange = useCallback(changes => setNodes(nds => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback(changes => setEdges(eds => applyEdgeChanges(changes, eds)), []);

  const focusNode = useCallback(
    node => {
      if (!node) {
        fitView({ duration: 700 });
        return;
      }
      const currentNode = getNode(node.id);
      if (!currentNode) {
        return;
      }
      const width = currentNode.measured?.width ?? 0;
      const height = currentNode.measured?.height ?? 0;
      const x = currentNode.position.x + width / 2;
      const y = currentNode.position.y + height / 2;
      setCenter(x, y, { zoom: 1, duration: 700 });
    },
    [fitView, getNode, setCenter],
  );

  const runLayout = useCallback(() => {
    const layouted = getLayoutedElements(nodes, edges);
    setNodes(layouted.nodes);
    setEdges(layouted.edges);
  }, [nodes, edges]);

  useEffect(() => {
    actions.chatFlowIntents.load();
  }, [actions.chatFlowIntents]);

  useEffect(() => {
    if (flow?._id) {
      actions.chatFlows.find(flow._id);
    }
  }, [actions.chatFlows, flow?._id]);

  useEffect(() => {
    const graph = buildFlowGraph(flow);
    setNodes(graph.nodes);
    setEdges(graph.edges);
    setLayoutState('not done');
  }, [flow]);

  useEffect(() => {
    if (layoutState === 'not done') {
      setLayoutState('pending');
    }
  }, [nodes, edges, layoutState]);

  useEffect(() => {
    if (layoutState === 'pending' && nodes[0]?.measured?.width > 0) {
      setLayoutState('done');
      runLayout();
      setTimeout(() => {
        fitView({ duration: 700 });
      }, 20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run layout once when first node dimensions are ready
  }, [layoutState, nodes.length, nodes[0]?.measured?.width]);

  useEffect(() => {
    if (!flowReady) {
      return;
    }
    setTimeout(() => {
      const selectedNode = context.stack[0] && nodes?.find(n => n.id === context.stack[0]?.page);
      focusNode(selectedNode);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focus selected node when flow instance mounts; context.current is a mutable ref
  }, [flowReady, context.current]);

  const onInit = useCallback(() => {
    setFlowReady(true);
  }, []);

  const trainNLU = () => {
    actions.chatFlows.train(flow._id);
  };

  if (!flow) {
    return null;
  }
  return (
    <Box sx={styles.root}>
      <Toolbar sx={styles.toolbar}>
        <IconButton onClick={() => zoomIn()} size="large">
          <ZoomInOutlineIcon />
        </IconButton>
        <IconButton onClick={() => zoomOut()} size="large">
          <ZoomOutOutlineIcon />
        </IconButton>
        <IconButton onClick={() => fitView({ duration: 700 })} size="large">
          <FitToPageOutlineIcon />
        </IconButton>
        <IconButton onClick={runLayout} size="large">
          <GraphOutlineIcon />
        </IconButton>
        <Box sx={styles.toolbarCenter} />
        {!flow.lastVersionTrained && (
          <Typography color="error" sx={styles.errorMessage}>
            <SvgIcon sx={styles.warningIcon}>
              <AlertCircleCheckOutlineIcon />
            </SvgIcon>
            {I18n.t('ChatFlowSettings.Needs training')}
          </Typography>
        )}
        <Button variant="text" color="secondary" onClick={trainNLU}>
          <SvgIcon sx={flow.training ? styles.trainingIcon : styles.icon}>
            {flow.training ? <LoadingIcon /> : <BrainFreezeOutlineIcon />}
          </SvgIcon>
          {I18n.t('ChatFlowSettings.Train NLU')}
        </Button>
        <Button
          variant="text"
          color="secondary"
          onClick={() => context.type !== 'test' && context.push({ type: 'test' })}
        >
          <SvgIcon sx={styles.icon}>
            <CommentTextMultipleIcon />
          </SvgIcon>
          {I18n.t('ChatFlowSettings.Test flow')}
        </Button>
      </Toolbar>
      <Box sx={styles.flow}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onInit={onInit}
          elementsSelectable
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          snapToGrid
          defaultMarkerColor="currentColor"
          nodesConnectable={false}
          style={{ width: '100%', height: '100%' }}
          maxZoom={1}
        >
          {!context.current && <MiniMap />}
          <Background style={styles.background} color="#ccc" />
        </ReactFlow>
      </Box>
    </Box>
  );
};
ChatFlowEdit.propTypes = {
  flow: object.isRequired,
  actions: object.isRequired,
};
export default connector(props => (
  <ReactFlowProvider>
    <ChatFlowEdit {...props} />
  </ReactFlowProvider>
));
